/**
 * Centralized File Types & Detection Utility for CampNote Frontend
 * Supports all 23 canonical file extensions across Documents, Images, Video, Audio.
 */

export const FILE_EXTENSIONS = {
  // Documents
  PDF: 'pdf',
  DOC: 'doc',
  DOCX: 'docx',
  PPT: 'ppt',
  PPTX: 'pptx',
  XLS: 'xls',
  XLSX: 'xlsx',
  TXT: 'txt',
  CSV: 'csv',

  // Images
  JPG: 'jpg',
  JPEG: 'jpeg',
  PNG: 'png',
  WEBP: 'webp',
  GIF: 'gif',
  SVG: 'svg',

  // Video
  MP4: 'mp4',
  WEBM: 'webm',
  MOV: 'mov',
  M4V: 'm4v',

  // Audio
  MP3: 'mp3',
  WAV: 'wav',
  OGG: 'ogg',
  M4A: 'm4a',
};

export const FORMAT_DETAILS = {
  pdf: { label: 'PDF', category: 'document', mime: 'application/pdf', badgeClass: 'format-badge--pdf', previewable: true },
  doc: { label: 'Word (DOC)', category: 'office', mime: 'application/msword', badgeClass: 'format-badge--word', previewable: true },
  docx: { label: 'Word (DOCX)', category: 'office', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', badgeClass: 'format-badge--word', previewable: true },
  ppt: { label: 'PowerPoint (PPT)', category: 'presentation', mime: 'application/vnd.ms-powerpoint', badgeClass: 'format-badge--ppt', previewable: true },
  pptx: { label: 'PowerPoint (PPTX)', category: 'presentation', mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', badgeClass: 'format-badge--ppt', previewable: true },
  xls: { label: 'Excel (XLS)', category: 'spreadsheet', mime: 'application/vnd.ms-excel', badgeClass: 'format-badge--excel', previewable: true },
  xlsx: { label: 'Excel (XLSX)', category: 'spreadsheet', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', badgeClass: 'format-badge--excel', previewable: true },
  txt: { label: 'Text (TXT)', category: 'text', mime: 'text/plain', badgeClass: 'format-badge--txt', previewable: true },
  csv: { label: 'CSV Table', category: 'csv', mime: 'text/csv', badgeClass: 'format-badge--csv', previewable: true },

  jpg: { label: 'JPG', category: 'image', mime: 'image/jpeg', badgeClass: 'format-badge--image', previewable: true },
  jpeg: { label: 'JPEG', category: 'image', mime: 'image/jpeg', badgeClass: 'format-badge--image', previewable: true },
  png: { label: 'PNG', category: 'image', mime: 'image/png', badgeClass: 'format-badge--image', previewable: true },
  webp: { label: 'WEBP', category: 'image', mime: 'image/webp', badgeClass: 'format-badge--image', previewable: true },
  gif: { label: 'GIF', category: 'image', mime: 'image/gif', badgeClass: 'format-badge--image', previewable: true },
  svg: { label: 'SVG', category: 'image', mime: 'image/svg+xml', badgeClass: 'format-badge--image', previewable: true },

  mp4: { label: 'MP4 Video', category: 'video', mime: 'video/mp4', badgeClass: 'format-badge--video', previewable: true },
  webm: { label: 'WEBM Video', category: 'video', mime: 'video/webm', badgeClass: 'format-badge--video', previewable: true },
  mov: { label: 'QuickTime MOV', category: 'video', mime: 'video/quicktime', badgeClass: 'format-badge--video', previewable: true },
  m4v: { label: 'M4V Video', category: 'video', mime: 'video/x-m4v', badgeClass: 'format-badge--video', previewable: true },

  mp3: { label: 'MP3 Audio', category: 'audio', mime: 'audio/mpeg', badgeClass: 'format-badge--audio', previewable: true },
  wav: { label: 'WAV Audio', category: 'audio', mime: 'audio/wav', badgeClass: 'format-badge--audio', previewable: true },
  ogg: { label: 'OGG Audio', category: 'audio', mime: 'audio/ogg', badgeClass: 'format-badge--audio', previewable: true },
  m4a: { label: 'M4A Audio', category: 'audio', mime: 'audio/mp4', badgeClass: 'format-badge--audio', previewable: true },
};

export const ALL_SUPPORTED_EXTS = Object.keys(FORMAT_DETAILS);

/**
 * Extracts extension from filename, doc, or URL
 */
export function getFileExtension(fileNameOrDoc) {
  if (!fileNameOrDoc) return 'pdf';

  if (typeof fileNameOrDoc === 'object') {
    if (fileNameOrDoc.fileExtension) return fileNameOrDoc.fileExtension.toLowerCase().replace(/^\./, '');
    if (fileNameOrDoc.fileFormat) return fileNameOrDoc.fileFormat.toLowerCase().replace(/^\./, '');
    if (fileNameOrDoc.originalFileName) return getFileExtension(fileNameOrDoc.originalFileName);
    if (fileNameOrDoc.title) return getFileExtension(fileNameOrDoc.title);
    if (fileNameOrDoc.cloudinaryUrl) return getFileExtension(fileNameOrDoc.cloudinaryUrl);
    return 'pdf';
  }

  const str = String(fileNameOrDoc).split('?')[0].split('#')[0].trim();
  const lastDot = str.lastIndexOf('.');
  if (lastDot === -1 || lastDot === str.length - 1) return 'pdf';
  return str.substring(lastDot + 1).toLowerCase();
}

/**
 * Derives MIME type: MIME metadata -> extension lookup -> safe fallback
 */
export function getMimeType(docOrFile) {
  if (!docOrFile) return 'application/octet-stream';
  if (typeof docOrFile === 'object' && docOrFile.mimeType && docOrFile.mimeType !== 'application/octet-stream') {
    return docOrFile.mimeType;
  }
  const ext = getFileExtension(docOrFile);
  return FORMAT_DETAILS[ext]?.mime || 'application/octet-stream';
}

/**
 * Derives file category: document, image, video, audio, text, csv, office, presentation, spreadsheet
 */
export function getFileCategory(docOrFile) {
  const mime = typeof docOrFile === 'object' ? docOrFile.mimeType : '';
  if (mime) {
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    if (mime === 'text/plain') return 'text';
    if (mime === 'text/csv') return 'csv';
    if (mime === 'application/pdf') return 'document';
  }

  const ext = getFileExtension(docOrFile);
  return FORMAT_DETAILS[ext]?.category || 'document';
}

export function isPdf(docOrFile) {
  return getFileExtension(docOrFile) === 'pdf';
}

export function isImage(docOrFile) {
  const ext = getFileExtension(docOrFile);
  return ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(ext);
}

export function isVideo(docOrFile) {
  const ext = getFileExtension(docOrFile);
  return ['mp4', 'webm', 'mov', 'm4v'].includes(ext);
}

export function isAudio(docOrFile) {
  const ext = getFileExtension(docOrFile);
  return ['mp3', 'wav', 'ogg', 'm4a'].includes(ext);
}

export function isText(docOrFile) {
  const ext = getFileExtension(docOrFile);
  return ext === 'txt';
}

export function isCsv(docOrFile) {
  const ext = getFileExtension(docOrFile);
  return ext === 'csv';
}

export function isOfficeDocument(docOrFile) {
  const ext = getFileExtension(docOrFile);
  return ['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(ext);
}

export function isPreviewSupported(docOrFile) {
  const ext = getFileExtension(docOrFile);
  return Boolean(FORMAT_DETAILS[ext]?.previewable);
}

export function getFormatLabel(docOrFile) {
  const ext = getFileExtension(docOrFile);
  return FORMAT_DETAILS[ext]?.label || ext.toUpperCase();
}

export function getFormatBadgeClass(docOrFile) {
  const ext = getFileExtension(docOrFile);
  return FORMAT_DETAILS[ext]?.badgeClass || `format-badge--${ext}`;
}

export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
